import React, { useState } from 'react';
import { Button, Alert, Card } from '@/components/common';
import { Language, PaymentPreference, ServiceCategory } from '@/types';
import { getTranslation, SERVICE_CATEGORIES, SERVICE_TYPES, PAYMENT_PREFERENCES } from '@/constants';
import { useServices } from '@/hooks';
import { useAuth } from '@/context/AuthContext';

interface ServiceOfferingFormProps {
  language: Language;
}

type DayOfWeek = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';
type TimeSlot = 'morning' | 'afternoon' | 'evening';

interface AvailabilitySlot {
  day: DayOfWeek;
  time: TimeSlot;
}

export const ServiceOfferingForm: React.FC<ServiceOfferingFormProps> = ({ language }) => {
  const { user, userProfile } = useAuth();
  const { createOffering } = useServices(user?.uid);
  const t = getTranslation(language);

  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [paymentPreferences, setPaymentPreferences] = useState<PaymentPreference[]>([]);
  const [availability, setAvailability] = useState<AvailabilitySlot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const days: DayOfWeek[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const timeSlots: TimeSlot[] = ['morning', 'afternoon', 'evening'];

  const toggleAvailability = (day: DayOfWeek, time: TimeSlot) => {
    const exists = availability.some((slot) => slot.day === day && slot.time === time);
    if (exists) {
      setAvailability((prev) => prev.filter((slot) => !(slot.day === day && slot.time === time)));
    } else {
      setAvailability((prev) => [...prev, { day, time }]);
    }
  };

  const isSlotSelected = (day: DayOfWeek, time: TimeSlot) => {
    return availability.some((slot) => slot.day === day && slot.time === time);
  };

  const formatAvailabilityForStorage = (): string => {
    return JSON.stringify(availability);
  };

  const toggleService = (serviceId: string) => {
    setSelectedServices((prev) =>
      prev.includes(serviceId) ? prev.filter((id) => id !== serviceId) : [...prev, serviceId]
    );
  };

  const togglePayment = (payment: PaymentPreference) => {
    setPaymentPreferences((prev) =>
      prev.includes(payment) ? prev.filter((p) => p !== payment) : [...prev, payment]
    );
  };

  const handleSubmit = async () => {
    if (selectedServices.length === 0) {
      setError('Please select at least one service');
      return;
    }

    if (paymentPreferences.length === 0) {
      setError('Please select at least one payment preference');
      return;
    }

    if (!user || !userProfile) {
      setError('You must be signed in');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const offeringData = {
        userId: user.uid,
        userName: userProfile.displayName || userProfile.email,
        userEmail: userProfile.email,
        userPhotoURL: userProfile.photoURL,
        userCommunityStatus: userProfile.communityStatus,
        userTotalExchanges: userProfile.totalExchanges,
        serviceTypes: selectedServices,
        paymentPreferences,
        availability: formatAvailabilityForStorage(),
        // Only include postalCode if it exists (Firebase doesn't accept undefined)
        ...(userProfile.postalCode && { userPostalCode: userProfile.postalCode }),
      };

      await createOffering(offeringData);

      setSuccess(true);
      setSelectedServices([]);
      setPaymentPreferences([]);
      setAvailability([]);

      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError('Failed to submit offering. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const groupedServices = Object.entries(SERVICE_CATEGORIES).map(([category, names]) => ({
    category: category as ServiceCategory,
    name: names[language],
    services: SERVICE_TYPES.filter((st) => st.category === category),
  }));

  return (
    <div className="space-y-4">
      {success && <Alert variant="success">{t.offerSent}</Alert>}
      {error && (
        <Alert variant="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <h3 className="text-base font-semibold text-gray-900 mb-3">{t.selectServices}</h3>

        <div className="space-y-4">
          {groupedServices.map((group) => (
            <div key={group.category}>
              <h4 className="text-sm font-medium text-gray-800 mb-2">{group.name}</h4>
              <div className="grid grid-cols-1 gap-1">
                {group.services.map((service) => (
                  <label
                    key={service.id}
                    className="flex items-center space-x-2 p-2 rounded hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedServices.includes(service.id)}
                      onChange={() => toggleService(service.id)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">
                      {service.icon} {service.name[language]}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <h3 className="text-base font-semibold text-gray-900 mb-3">{t.preferredPayment}</h3>
        <div className="grid grid-cols-2 gap-2">
          {PAYMENT_PREFERENCES.map((pref) => {
            const prefKey = pref.key as PaymentPreference;
            const label =
              prefKey === 'cash'
                ? t.cad
                : prefKey === 'points'
                ? t.communityPoints
                : t.eitherOption;

            return (
              <label
                key={pref.key}
                className="flex items-center space-x-2 p-2 border rounded-lg cursor-pointer transition-colors hover:border-blue-300"
                style={{
                  borderColor: paymentPreferences.includes(prefKey) ? '#3B82F6' : '#E5E7EB',
                  backgroundColor: paymentPreferences.includes(prefKey) ? '#EFF6FF' : 'white',
                }}
              >
                <input
                  type="checkbox"
                  checked={paymentPreferences.includes(prefKey)}
                  onChange={() => togglePayment(prefKey)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-xs font-medium text-gray-700">
                  {pref.icon} {label}
                </span>
              </label>
            );
          })}
        </div>
      </Card>

      <Card>
        <h3 className="text-base font-semibold text-gray-900 mb-1">{t.availability}</h3>
        <p className="text-xs text-gray-500 mb-3">{t.availabilityDescription}</p>

        <div className="overflow-x-auto -mx-2 px-2">
          <table className="w-full border-collapse min-w-[280px]">
            <thead>
              <tr>
                <th className="p-1 text-left text-xs font-medium text-gray-500 w-16"></th>
                {timeSlots.map((time) => (
                  <th key={time} className="p-1 text-center text-xs font-medium text-gray-600">
                    {t[time]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {days.map((day) => (
                <tr key={day} className="border-t border-gray-100">
                  <td className="p-1 text-xs font-medium text-gray-600">{t[day].slice(0, 3)}</td>
                  {timeSlots.map((time) => (
                    <td key={`${day}-${time}`} className="p-1 text-center">
                      <button
                        type="button"
                        onClick={() => toggleAvailability(day, time)}
                        className={`
                          w-9 h-9 rounded-lg transition-all duration-200
                          focus:outline-none focus:ring-2 focus:ring-blue-300
                          ${isSlotSelected(day, time)
                            ? 'bg-blue-600 text-white shadow-sm'
                            : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                          }
                        `}
                        aria-label={`${t[day]} ${t[time]}`}
                        aria-pressed={isSlotSelected(day, time)}
                      >
                        {isSlotSelected(day, time) ? '✓' : ''}
                      </button>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {availability.length > 0 && (
          <p className="mt-2 text-xs text-gray-500">
            {availability.length} {language === 'en' ? 'slots selected' : 'créneaux'}
          </p>
        )}
      </Card>

      <Button
        onClick={handleSubmit}
        fullWidth
        size="md"
        loading={loading}
        disabled={selectedServices.length === 0 || paymentPreferences.length === 0}
      >
        {t.submit}
      </Button>
    </div>
  );
};
